import { type FC } from "react";

import { Card } from "@/components/ui/Card";

interface ScoreCardProperties {
  className?: string;
  description?: string;
  maxScore?: number;
  score: number;
  title: string;
  trend?: "down" | "stable" | "up";
  trendValue?: number;
}

/**
 * Score-Bewertung und Farben
 *
 * @param score - Current score value
 *
 * @param max - Maximum score value
 *
 * @returns Status object with colors and crown flag
 *
 */
function getScoreStatus(score: number, max: number) {
  const percentage = (score / max) * 100;

  if (percentage >= 85) {
    return {
      bgColor: "bg-green-500",
      color: "text-green-400",
      ringColor: "ring-green-500/20",
      showCrown: true,
      status: "excellent",
    };
  }
  if (percentage >= 70) {
    return {
      bgColor: "bg-yellow-500",
      color: "text-yellow-400",
      ringColor: "ring-yellow-500/20",
      showCrown: false,
      status: "good",
    };
  }
  return {
    bgColor: "bg-red-500",
    color: "text-red-400",
    ringColor: "ring-red-500/20",
    showCrown: false,
    status: "poor",
  };
}

/**
 * ScoreCard - Moderne Score-Anzeige für Oura-Scores
 *
 * Features:
 * - Verwendet globale Card-Komponente für konsistentes Styling
 * - Große, prominente Score-Anzeige
 * - Farbkodierung basierend auf Score-Bereichen
 * - Trend-Anzeige (optional)
 * - Responsive Design
 *
 * @param root0
 *
 * @param root0.title
 *
 * @param root0.score
 *
 * @param root0.maxScore
 *
 * @param root0.description
 *
 * @param root0.trend
 *
 * @param root0.trendValue
 *
 * @param root0.className
 *
 * @returns {JSX.Element} Score card component
 *
 */
export const ScoreCard: FC<ScoreCardProperties> = ({
  className = "",
  description,
  maxScore = 100,
  score,
  title,
  trend,
  trendValue,
}) => {
  const scoreStatus = getScoreStatus(score, maxScore);
  const percentage = Math.round((score / maxScore) * 100);

  // Trend className calculation
  const getTrendClassName = (): string => {
    if (trend === "up") {
      return "font-medium text-green-400";
    }
    if (trend === "down") {
      return "font-medium text-red-400";
    }
    return "font-medium text-neutral-400";
  };

  // Trend-Icon
  const getTrendIcon = (): null | string => {
    switch (trend) {
      case "up": {
        return "↗️";
      }
      case "down": {
        return "↘️";
      }
      case "stable": {
        return "→";
      }
      default: {
        return null;
      }
    }
  };

  return (
    <Card className={`text-center ${className}`} padding="md" variant="default">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-sm font-medium text-neutral-400 uppercase tracking-wide">{title}</h3>
        {description ? <p className="text-xs text-neutral-500 mt-1">{description}</p> : null}
      </div>

      {/* Score Display */}
      <div className="relative mb-4">
        {/* Crown (nur bei excellent scores) */}
        {scoreStatus.showCrown ? (
          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 z-10">
            <span className="text-lg">👑</span>
          </div>
        ) : null}

        {/* Score Circle */}
        <div
          className={`relative w-20 h-20 mx-auto rounded-full ${scoreStatus.bgColor} ${scoreStatus.ringColor} ring-4 flex items-center justify-center`}
        >
          <span className="text-2xl font-bold text-white">{score}</span>
        </div>
      </div>

      {/* Status Badge */}
      <div
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${scoreStatus.color} bg-neutral-800 border border-neutral-700`}
      >
        <span className="w-2 h-2 rounded-full bg-current mr-2" />
        {scoreStatus.status}
      </div>

      {/* Trend (optional) */}
      {trend && trendValue !== undefined ? (
        <div className="mt-3 flex items-center justify-center gap-1 text-xs">
          <span>{getTrendIcon()}</span>
          <span className={getTrendClassName()}>
            {trendValue > 0 ? `+${String(trendValue)}` : String(trendValue)}
          </span>
          <span className="text-neutral-500">vs. yesterday</span>
        </div>
      ) : null}

      {/* Progress Bar */}
      <div className="mt-4 w-full bg-neutral-700 rounded-full h-1.5">
        <div
          className={`h-1.5 rounded-full ${scoreStatus.bgColor} transition-all duration-500 ease-out`}
          style={{ width: `${String(percentage)}%` }}
        />
      </div>
    </Card>
  );
};
